import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  ColorPicker,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Switch,
  Tabs,
  Segmented,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { apiRequests } from "src/shared/api/api";
import "./CreateSegments.css";

const { Panel } = Collapse;
const { TextArea } = Input;

const AUTO_REPLY_CONDITION_TYPES = [
  { value: "not_registered", label: "Не зарегистрирован в справочнике" },
  { value: "no_loyalty_card", label: "Нет карты лояльности" },
];

const CreateSegments = ({
  visible,
  setVisible,
  onSuccess,
  initialValues: propInitialValues,
  editId,
  editMode = false,
}) => {
  const [form] = Form.useForm();
  const [updateType, setUpdateType] = useState("cron");
  const [activeTab, setActiveTab] = useState("form");
  const [jsonValue, setJsonValue] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [touchedFields, setTouchedFields] = useState(new Set());
  const [purchasesError, setPurchasesError] = useState(false);
  const [existingSegmentNames, setExistingSegmentNames] = useState([]);

  // Автоответ
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyConditions, setAutoReplyConditions] = useState([]);

  // NEW: тип сегмента (standard или auto_reply)
  const [segmentType, setSegmentType] = useState("standard");

  const deepClean = useCallback(
    (obj, isRoot = true, currentTouchedFields = touchedFields) => {
      try {
        if (obj === null || typeof obj !== "object") return obj;
        if (Array.isArray(obj)) {
          const cleanedArray = obj
            .map((item) => deepClean(item, false, currentTouchedFields))
            .filter(
              (item) =>
                item !== null &&
                item !== undefined &&
                item !== "" &&
                !(typeof item === "object" && Object.keys(item).length === 0),
            );
          return cleanedArray.length > 0 ? cleanedArray : undefined;
        }
        const cleaned = {};
        let hasValues = false;
        for (const key in obj) {
          const value = obj[key];
          if (value !== null && value !== undefined && value !== "") {
            const cleanedValue = deepClean(value, false, currentTouchedFields);
            const isValidValue =
              cleanedValue !== null &&
              cleanedValue !== undefined &&
              cleanedValue !== "" &&
              !(
                typeof cleanedValue === "object" &&
                Object.keys(cleanedValue).length === 0
              );
            if (isValidValue || cleanedValue === false) {
              if (isRoot) {
                if (!currentTouchedFields.has(key)) {
                  if (key === "type_of_update" && cleanedValue === "cron")
                    continue;
                  if (key === "update_settings") {
                    if (obj.type_of_update === "request") continue;
                    if (
                      cleanedValue?.interval_minutes === 5 &&
                      Object.keys(cleanedValue).length === 1
                    )
                      continue;
                  }
                  if (key === "is_archived" && cleanedValue === false) continue;
                }
              }
              cleaned[key] = cleanedValue;
              hasValues = true;
            }
          }
        }
        return hasValues ? cleaned : undefined;
      } catch (error) {
        console.error("Ошибка в deepClean:", error);
        return obj;
      }
    },
    [touchedFields],
  );

  const handleFieldFocus = (fieldName) => {
    setTouchedFields((prev) => new Set([...prev, fieldName]));
  };

  const isDateObject = (val) => dayjs.isDayjs(val) || moment.isMoment(val);
  const formatDate = (val) => {
    if (dayjs.isDayjs(val)) return val.format("YYYY-MM-DD");
    if (moment.isMoment(val)) return val.format("YYYY-MM-DD");
    return val;
  };

  const transformToFormFormat = (values) => {
    if (!values) return values;
    const transformed = JSON.parse(JSON.stringify(values));
    if (transformed?.criteria?.purchases?.date_range) {
      const dr = transformed.criteria.purchases.date_range;
      transformed.criteria.purchases.date_range = [
        dr.gte ? dayjs(dr.gte, "YYYY-MM-DD") : null,
        dr.lte ? dayjs(dr.lte, "YYYY-MM-DD") : null,
      ];
    }
    if (transformed?.criteria?.created_at) {
      const dr = transformed.criteria.created_at;
      transformed.criteria.created_at = {
        ...dr,
        gte: dr.gte ? dayjs(dr.gte, "YYYY-MM-DD") : null,
        lte: dr.lte ? dayjs(dr.lte, "YYYY-MM-DD") : null,
      };
    }
    ["picker", "courier"].forEach((role) => {
      ["start", "finish"].forEach((key) => {
        const dt = transformed?.criteria?.[role]?.[key];
        if (dt) {
          transformed.criteria[role][key] = {
            ...dt,
            gte: dt.gte ? dayjs(dt.gte, "YYYY-MM-DD") : null,
            lte: dt.lte ? dayjs(dt.lte, "YYYY-MM-DD") : null,
          };
        }
      });
    });
    if (transformed?.criteria?.delivery_info?.delivery_date) {
      const dd = transformed.criteria.delivery_info.delivery_date;
      transformed.criteria.delivery_info.delivery_date = {
        ...dd,
        gte: dd.gte ? dayjs(dd.gte, "YYYY-MM-DD") : null,
        lte: dd.lte ? dayjs(dd.lte, "YYYY-MM-DD") : null,
      };
    }
    return transformed;
  };

  const deepCopyPreserveDates = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (isDateObject(obj)) return obj.clone();
    if (typeof obj !== "object") return obj;
    if (Array.isArray(obj))
      return obj.map((item) => deepCopyPreserveDates(item));
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key))
        result[key] = deepCopyPreserveDates(obj[key]);
    }
    return result;
  };

  const transformToApiFormat = (values) => {
    if (!values) return values;
    const transformed = deepCopyPreserveDates(values);
    if (Array.isArray(transformed?.criteria?.purchases?.date_range)) {
      const [gte, lte] = transformed.criteria.purchases.date_range;
      const dateRange = {};
      if (gte && isDateObject(gte)) dateRange.gte = formatDate(gte);
      else if (gte && typeof gte === "string") dateRange.gte = gte;
      if (lte && isDateObject(lte)) dateRange.lte = formatDate(lte);
      else if (lte && typeof lte === "string") dateRange.lte = lte;
      if (Object.keys(dateRange).length > 0)
        transformed.criteria.purchases.date_range = dateRange;
      else delete transformed.criteria.purchases.date_range;
    }
    const formatDateField = (obj) => {
      if (!obj) return obj;
      const result = { ...obj };
      if (isDateObject(result.gte)) result.gte = formatDate(result.gte);
      if (isDateObject(result.lte)) result.lte = formatDate(result.lte);
      return result;
    };
    if (transformed?.criteria?.created_at)
      transformed.criteria.created_at = formatDateField(
        transformed.criteria.created_at,
      );
    ["picker", "courier"].forEach((role) => {
      ["start", "finish"].forEach((key) => {
        if (transformed?.criteria?.[role]?.[key])
          transformed.criteria[role][key] = formatDateField(
            transformed.criteria[role][key],
          );
      });
    });
    if (transformed?.criteria?.delivery_info?.delivery_date) {
      transformed.criteria.delivery_info.delivery_date = formatDateField(
        transformed.criteria.delivery_info.delivery_date,
      );
    }
    return transformed;
  };

  const getInitialValues = () => ({
    name: null,
    criteria: {},
    type_of_update: "cron",
    update_settings: { interval_minutes: 5 },
    is_archived: false,
    actions: {},
  });

  const getSelectOptions = useCallback(() => {
    apiRequests.nomenclature.getItems().then((data) => {
      setNomenclatures(data.data.result.map((n) => n.name));
    });
    apiRequests.categories.getItems().then((data) => {
      setCategories(data.data.result.map((c) => c.name));
    });

    apiRequests.chats
      .getChannels({ limit: 100 })
      .then((res) => {
        setChannels(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setChannels([]));
  }, []);

  // NEW: синхронизация auto_reply из initialValues
  const syncAutoReplyFromValues = (values) => {
    const ar = values?.auto_reply;
    if (ar) {
      setAutoReplyEnabled(!!ar.enabled);
      setAutoReplyConditions(Array.isArray(ar.conditions) ? ar.conditions : []);
    } else {
      setAutoReplyEnabled(false);
      setAutoReplyConditions([]);
    }
  };

  // MODIFIED: при загрузке сегмента определяем тип и заполняем вкладки
  useEffect(() => {
    if (visible) {
      apiRequests.segments
        .getData()
        .then((res) => {
          const names = (res.data || [])
            .map((s) => s.name?.toLowerCase())
            .filter(Boolean);
          setExistingSegmentNames(names);
        })
        .catch(() => {});

      if (editMode || propInitialValues) {
        let values = propInitialValues || getInitialValues();
        values = transformToFormFormat(values);
        form.setFieldsValue(values);
        setUpdateType(values.type_of_update || "cron");
        syncAutoReplyFromValues(values);

        // NEW: определяем тип сегмента по наличию auto_reply
        if (values.auto_reply && Object.keys(values.auto_reply).length > 0) {
          setSegmentType("auto_reply");
        } else {
          setSegmentType("standard");
        }

        const apiFormattedValues = transformToApiFormat(values);
        const cleanedValues = deepClean(apiFormattedValues, true, new Set());
        setJsonValue(JSON.stringify(cleanedValues || {}, null, 2));
      } else {
        const initialValues = getInitialValues();
        form.setFieldsValue(initialValues);
        setUpdateType("cron");
        setAutoReplyEnabled(false);
        setAutoReplyConditions([]);
        setSegmentType("standard");
        setJsonValue("{}");
      }
      setActiveTab("form");
      getSelectOptions();
      setJsonError(null);
    } else {
      form.resetFields();
      setUpdateType("cron");
      setActiveTab("form");
      setJsonValue("{}");
      setJsonError(null);
      setAutoReplyEnabled(false);
      setAutoReplyConditions([]);
      setSegmentType("standard");
      setTimeout(() => {
        setTouchedFields(new Set());
        setPurchasesError(false);
      }, 0);
    } // eslint-disable-next-line
  }, [visible, propInitialValues, form, getSelectOptions, editMode]);

  const handleFormChange = (changedValues, allValues) => {
    const changedField = Object.keys(changedValues)[0];
    if (
      changedField === "criteria" &&
      purchasesError &&
      hasPurchasesData(allValues)
    ) {
      setPurchasesError(false);
    }
    setTouchedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(changedField);
      if (changedField === "type_of_update") {
        newSet.add("update_settings");
        if (changedValues.type_of_update === "request")
          form.setFieldsValue({ update_settings: undefined });
      }
      if (changedField === "is_archived") newSet.add("is_archived");
      return newSet;
    });
    try {
      const apiFormattedValues = transformToApiFormat(allValues);
      const cleanedValues = deepClean(apiFormattedValues, true, touchedFields);
      if (
        !editMode &&
        !propInitialValues &&
        Object.keys(cleanedValues || {}).length === 0
      ) {
        setJsonValue("{}");
      } else {
        setJsonValue(JSON.stringify(cleanedValues || {}, null, 2));
      }
      setJsonError(null);
    } catch (error) {
      setJsonError("Ошибка преобразования в JSON");
    }
  };

  const handleJsonChange = (e) => {
    const newValue = e.target.value;
    setJsonValue(newValue);
    try {
      if (newValue.trim() === "{}" || newValue.trim() === "") {
        form.setFieldsValue(getInitialValues());
        setJsonError(null);
        return;
      }
      const parsed = JSON.parse(newValue);
      const cleanedParsed = deepClean(parsed, true, touchedFields) || {};
      const formFormattedValues = transformToFormFormat(cleanedParsed);
      form.setFieldsValue(formFormattedValues);
      syncAutoReplyFromValues(formFormattedValues);
      setJsonError(null);
    } catch (error) {
      setJsonError("Невалидный JSON");
    }
  };

  const formatValues = (values) => {
    const cleanEmptyValues = (obj) => {
      if (!obj) return undefined;
      const cleaned = { ...obj };
      for (const key in cleaned) {
        if (
          cleaned[key] === null ||
          cleaned[key] === "" ||
          cleaned[key] === undefined
        )
          delete cleaned[key];
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    };
    if (!values.criteria) return { ...values };
    const rawPurchases = values.criteria.purchases;
    let purchases = undefined;
    if (rawPurchases) {
      const totalAmount = cleanEmptyValues(rawPurchases.total_amount);
      const count = cleanEmptyValues(rawPurchases.count);
      const lastPurchaseDaysAgo = cleanEmptyValues(
        rawPurchases.last_purchase_days_ago,
      );
      const amountPerCheck = cleanEmptyValues(rawPurchases.amount_per_check);
      const cats =
        Array.isArray(rawPurchases.categories) &&
        rawPurchases.categories.length > 0
          ? rawPurchases.categories
          : undefined;
      const noms =
        Array.isArray(rawPurchases.nomenclatures) &&
        rawPurchases.nomenclatures.length > 0
          ? rawPurchases.nomenclatures
          : undefined;
      const dateRange = rawPurchases.date_range || undefined;
      const purchasesObj = {
        ...rawPurchases,
        ...(totalAmount ? { total_amount: totalAmount } : {}),
        ...(count ? { count } : {}),
        ...(lastPurchaseDaysAgo
          ? { last_purchase_days_ago: lastPurchaseDaysAgo }
          : {}),
        ...(amountPerCheck ? { amount_per_check: amountPerCheck } : {}),
        ...(cats ? { categories: cats } : {}),
        ...(noms ? { nomenclatures: noms } : {}),
        ...(dateRange ? { date_range: dateRange } : {}),
      };
      for (const key of Object.keys(purchasesObj)) {
        if (
          purchasesObj[key] === undefined ||
          purchasesObj[key] === null ||
          (Array.isArray(purchasesObj[key]) &&
            purchasesObj[key].length === 0) ||
          (typeof purchasesObj[key] === "object" &&
            !Array.isArray(purchasesObj[key]) &&
            Object.keys(purchasesObj[key]).length === 0)
        ) {
          delete purchasesObj[key];
        }
      }
      if (Object.keys(purchasesObj).length > 0) purchases = purchasesObj;
    }
    const rawLoyality = values.criteria.loyality;
    let loyality = undefined;
    if (rawLoyality) {
      const balance = cleanEmptyValues(rawLoyality.balance);
      const expiresInDays = cleanEmptyValues(rawLoyality.expires_in_days);
      const loyalityObj = {
        ...rawLoyality,
        ...(balance ? { balance } : {}),
        ...(expiresInDays ? { expires_in_days: expiresInDays } : {}),
      };
      for (const key of Object.keys(loyalityObj)) {
        if (
          loyalityObj[key] === undefined ||
          loyalityObj[key] === null ||
          (typeof loyalityObj[key] === "object" &&
            Object.keys(loyalityObj[key]).length === 0)
        )
          delete loyalityObj[key];
      }
      if (Object.keys(loyalityObj).length > 0) loyality = loyalityObj;
    }
    const rawDelivery = values.criteria.delivery_info;
    let deliveryInfo = undefined;
    if (rawDelivery) {
      const deliveryDate = cleanEmptyValues(rawDelivery.delivery_date);
      const recipient = cleanEmptyValues(rawDelivery.recipient);
      const deliveryObj = {
        ...rawDelivery,
        ...(deliveryDate ? { delivery_date: deliveryDate } : {}),
        ...(recipient ? { recipient } : {}),
      };
      for (const key of Object.keys(deliveryObj)) {
        if (
          deliveryObj[key] === undefined ||
          deliveryObj[key] === null ||
          (typeof deliveryObj[key] === "object" &&
            Object.keys(deliveryObj[key]).length === 0)
        )
          delete deliveryObj[key];
      }
      if (Object.keys(deliveryObj).length > 0) deliveryInfo = deliveryObj;
    }
    const criteriaObj = { ...values.criteria };
    delete criteriaObj.purchases;
    delete criteriaObj.loyality;
    delete criteriaObj.delivery_info;
    if (purchases) criteriaObj.purchases = purchases;
    if (loyality) criteriaObj.loyality = loyality;
    if (deliveryInfo) criteriaObj.delivery_info = deliveryInfo;
    for (const key of Object.keys(criteriaObj)) {
      if (
        criteriaObj[key] === undefined ||
        criteriaObj[key] === null ||
        (typeof criteriaObj[key] === "object" &&
          !Array.isArray(criteriaObj[key]) &&
          Object.keys(criteriaObj[key]).length === 0)
      ) {
        delete criteriaObj[key];
      }
    }
    return {
      ...values,
      ...(Object.keys(criteriaObj).length > 0 ? { criteria: criteriaObj } : {}),
    };
  };

  const formatActions = (actions) => {
    if (!actions || typeof actions !== "object") return undefined;
    const cleanedActions = {
      ...(actions.add_existed_tags?.name?.length > 0
        ? { add_existed_tags: actions.add_existed_tags }
        : {}),
      ...(actions.remove_tags?.name?.length > 0
        ? { remove_tags: actions.remove_tags }
        : {}),
      ...(actions.add_docs_sales_tags?.tags?.length > 0
        ? { add_docs_sales_tags: actions.add_docs_sales_tags }
        : {}),
      ...(actions.remove_docs_sales_tags?.tags?.length > 0
        ? { remove_docs_sales_tags: actions.remove_docs_sales_tags }
        : {}),
      ...(actions.client_tags?.tags?.length > 0
        ? {
            client_tags: {
              tags: actions.client_tags.tags
                .map((tag) => {
                  const c = deepClean(tag);
                  return c || undefined;
                })
                .filter(Boolean),
            },
          }
        : {}),
      ...(actions.send_tg_notification &&
      (actions.send_tg_notification.message ||
        actions.send_tg_notification.user_tag ||
        actions.send_tg_notification.send_to)
        ? { send_tg_notification: deepClean(actions.send_tg_notification) }
        : {}),
      ...(actions.transform_loyality_card &&
      (actions.transform_loyality_card.cashback_percent > 0 ||
        actions.transform_loyality_card.max_withdraw_percentage > 0 ||
        actions.transform_loyality_card.lifetime > 0 ||
        actions.transform_loyality_card.tag)
        ? {
            transform_loyality_card: deepClean(actions.transform_loyality_card),
          }
        : {}),
      ...(actions.add_loyality_transaction &&
      (actions.add_loyality_transaction.amount > 0 ||
        actions.add_loyality_transaction.comment)
        ? {
            add_loyality_transaction: deepClean(
              actions.add_loyality_transaction,
            ),
          }
        : {}),
      ...(actions.send_wa_notification &&
      (actions.send_wa_notification.message ||
        actions.send_wa_notification.wappi_token ||
        actions.send_wa_notification.wappi_profile_id)
        ? { send_wa_notification: deepClean(actions.send_wa_notification) }
        : {}),
      ...(actions.do_http_request && actions.do_http_request.url
        ? { do_http_request: deepClean(actions.do_http_request) }
        : {}),
    };
    return Object.keys(cleanedActions).length > 0 ? cleanedActions : undefined;
  };

  // NEW: сборка auto_reply из формы
  const buildAutoReply = (formValues) => {
    const ar = formValues?.auto_reply;
    if (!ar?.channel_id) return undefined;
    const validConditions = autoReplyConditions.filter(
      (c) => c.type && c.button_text?.trim(),
    );
    if (!autoReplyEnabled && validConditions.length === 0) return undefined;
    return {
      enabled: autoReplyEnabled,
      channel_id: ar.channel_id,
      greeting_text: ar.greeting_text || undefined,
      send_only_once: ar.send_only_once !== false,
      conditions: validConditions,
    };
  };

  const hasPurchasesData = (values) => {
    const p = values?.criteria?.purchases;
    if (!p) return false;
    if (p.total_amount?.gte || p.total_amount?.lte || p.total_amount?.eq)
      return true;
    if (p.count?.gte || p.count?.lte || p.count?.eq) return true;
    if (
      p.amount_per_check?.gte ||
      p.amount_per_check?.lte ||
      p.amount_per_check?.eq
    )
      return true;
    if (p.last_purchase_days_ago?.gte || p.last_purchase_days_ago?.lte)
      return true;
    if (Array.isArray(p.date_range) && p.date_range.some((d) => d)) return true;
    if (Array.isArray(p.categories) && p.categories.length > 0) return true;
    if (Array.isArray(p.nomenclatures) && p.nomenclatures.length > 0)
      return true;
    return false;
  };

  const submitData = (formattedValues) => {
    const autoReply = buildAutoReply(form.getFieldsValue(true));
    const payload = { ...formattedValues };
    if (autoReply) payload.auto_reply = autoReply;
    else delete payload.auto_reply;

    // NEW: добавляем тип сегмента
    payload.type = segmentType;

    if (editId) {
      apiRequests.segments
        .putData(editId, payload)
        .then(() => {
          setVisible(false);
          message.success("Сегмент сохранён");
          onSuccess?.();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Не удалось сохранить сегмент",
          );
        });
    } else {
      apiRequests.segments
        .postData(payload)
        .then(() => {
          setVisible(false);
          message.success("Сегмент создан");
          onSuccess?.();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail || "Не удалось создать сегмент",
          );
        });
    }
  };

  const onFinish = (values) => {
    if (activeTab === "json") {
      try {
        const parsedJson = JSON.parse(jsonValue);
        const formattedValues = formatValues(parsedJson);
        if (formattedValues.actions)
          formattedValues.actions = formatActions(formattedValues.actions);
        submitData(formattedValues);
      } catch (error) {
        message.error("Ошибка в формате JSON данных");
      }
      return;
    }
    // NEW: для автоответа не проверяем purchases
    if (segmentType === "standard" && !hasPurchasesData(values)) {
      setPurchasesError(true);
      message.error(
        'Заполните хотя бы одно поле в разделе "Параметры покупок"',
      );
      form.scrollToField(["criteria", "purchases", "total_amount", "gte"], {
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setPurchasesError(false);
    const apiFormattedValues = transformToApiFormat(values);
    let formattedValues = formatValues(apiFormattedValues);
    if (formattedValues.actions)
      formattedValues.actions = formatActions(formattedValues.actions);
    // NEW: для автоответа очищаем criteria и actions, если они пусты
    if (segmentType === "auto_reply") {
      if (
        !formattedValues.criteria ||
        Object.keys(formattedValues.criteria).length === 0
      )
        delete formattedValues.criteria;
      if (
        !formattedValues.actions ||
        Object.keys(formattedValues.actions).length === 0
      )
        delete formattedValues.actions;
    }
    submitData(formattedValues);
  };

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields?.length > 0) {
      message.error(
        `Заполните обязательные поля: ${errorFields.map((f) => f.errors[0]).join(", ")}`,
      );
      setTimeout(() => {
        const allModals = document.querySelectorAll(".ant-modal-body");
        const modalBody = allModals[allModals.length - 1] || null;
        if (!modalBody) return;
        const firstErrorField = modalBody.querySelector(
          ".ant-form-item-has-error",
        );
        if (firstErrorField) {
          const scrollTop =
            modalBody.scrollTop +
            (firstErrorField.getBoundingClientRect().top -
              modalBody.getBoundingClientRect().top) -
            40;
          modalBody.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        }
      }, 200);
    }
  };

  const handleTabChange = (key) => {
    if (key === "json") {
      try {
        const values = form.getFieldsValue(true);
        const apiFormattedValues = transformToApiFormat(values);
        const cleanedValues = deepClean(
          apiFormattedValues,
          true,
          touchedFields,
        );

        const autoReply = buildAutoReply(values);
        if (autoReply) cleanedValues.auto_reply = autoReply;
        setJsonValue(JSON.stringify(cleanedValues || {}, null, 2));
        setJsonError(null);
      } catch (error) {
        setJsonError("Ошибка преобразования в JSON");
      }
    }
    setActiveTab(key);
  };

  // NEW: добавление/удаление условий автоответа
  const addCondition = () => {
    setAutoReplyConditions((prev) => [
      ...prev,
      { type: "not_registered", button_text: "" },
    ]);
  };

  const removeCondition = (idx) => {
    setAutoReplyConditions((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx, field, value) => {
    setAutoReplyConditions((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
    );
  };

  // NEW: рендер вкладки автоответа (отдельно от основной формы)
  const renderAutoReplyTab = () => (
    <div style={{ padding: "8px 0" }}>
      <div
        style={{
          background: "#f0f7ff",
          border: "1px solid #91caff",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 13, color: "#1677ff" }}>
          Когда клиент пишет боту, сегмент проверяет условия и автоматически
          отправляет кнопки. Клиент нажимает кнопку — данные создаются, кнопки
          исчезают.
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Switch
          checked={autoReplyEnabled}
          onChange={setAutoReplyEnabled}
          checkedChildren="Включён"
          unCheckedChildren="Выключен"
        />
        <span style={{ color: autoReplyEnabled ? "#1677ff" : "#999" }}>
          {autoReplyEnabled ? "Авто-ответ активен" : "Авто-ответ отключён"}
        </span>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Канал (бот)"
            name={["auto_reply", "channel_id"]}
            rules={
              autoReplyEnabled
                ? [{ required: true, message: "Выберите канал" }]
                : []
            }
          >
            <Select
              placeholder="Выберите канал"
              allowClear
              options={channels
                .filter((c) => c.type === "TELEGRAM")
                .map((c) => ({
                  value: c.id,
                  label: c.name || `Канал #${c.id}`,
                }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Отправлять кнопки"
            name={["auto_reply", "send_only_once"]}
            initialValue={true}
          >
            <Select>
              <Select.Option value={true}>Только один раз на чат</Select.Option>
              <Select.Option value={false}>При каждом сообщении</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Приветственное сообщение"
        name={["auto_reply", "greeting_text"]}
      >
        <Input.TextArea
          placeholder="Добро пожаловать! Для продолжения выберите действие:"
          rows={2}
          showCount
          maxLength={500}
        />
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: 13 }}>
        Условия и кнопки
      </Divider>

      {autoReplyConditions.length === 0 && (
        <div style={{ color: "#999", fontSize: 13, marginBottom: 12 }}>
          Добавьте хотя бы одно условие — при его выполнении клиент увидит
          кнопку.
        </div>
      )}

      {autoReplyConditions.map((cond, idx) => (
        <div
          key={idx}
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 10,
            background: "#fafafa",
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 6, fontSize: 12, color: "#666" }}>
              Условие #{idx + 1}
            </div>
            <Row gutter={10}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                  Тип условия
                </div>
                <Select
                  value={cond.type}
                  onChange={(v) => updateCondition(idx, "type", v)}
                  style={{ width: "100%" }}
                  options={AUTO_REPLY_CONDITION_TYPES}
                />
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                  Текст кнопки
                </div>
                <Input
                  value={cond.button_text}
                  onChange={(e) =>
                    updateCondition(idx, "button_text", e.target.value)
                  }
                  placeholder={
                    cond.type === "not_registered"
                      ? "📱 Зарегистрироваться"
                      : "🎁 Создать карту лояльности"
                  }
                />
              </Col>
            </Row>
            <div style={{ marginTop: 8, fontSize: 12, color: "#1677ff" }}>
              {cond.type === "not_registered"
                ? "→ Клиент не найден в справочнике. Появится кнопка запроса контакта (телефона)."
                : "→ У контрагента нет карты лояльности. Появится inline-кнопка создания карты."}
            </div>
          </div>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => removeCondition(idx)}
          />
        </div>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addCondition}
        style={{ width: "100%" }}
        disabled={
          autoReplyConditions.length >= AUTO_REPLY_CONDITION_TYPES.length
        }
      >
        Добавить условие
      </Button>

      {autoReplyConditions.length >= AUTO_REPLY_CONDITION_TYPES.length && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
          Все доступные условия добавлены.
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "inline-block" }}>
      {!editId && !editMode && (
        <Button onClick={() => setVisible(true)} type="primary">
          Создать сегмент
        </Button>
      )}

      <Modal
        open={visible}
        title={editId ? "Редактирование сегмента" : "Создание сегмента"}
        onCancel={() => setVisible(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setVisible(false)}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {editId ? "Сохранить" : "Создать"}
          </Button>,
        ]}
        width={900}
      >
        {/* NEW: выбор типа сегмента */}
        <Form.Item label="Тип сегмента" style={{ marginBottom: 16 }}>
          <Radio.Group
            value={segmentType}
            onChange={(e) => setSegmentType(e.target.value)}
            disabled={editMode} // при редактировании тип менять нельзя
          >
            <Radio.Button value="standard">Стандартный</Radio.Button>
            <Radio.Button value="auto_reply">Автоответ</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "form",
              label: "Основные",
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  onValuesChange={handleFormChange}
                  scrollToFirstError={{ behavior: "smooth", block: "center" }}
                >
                  <Form.Item
                    label="Название сегмента"
                    name="name"
                    rules={[
                      { required: true, message: "Введите название сегмента" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const currentName =
                            propInitialValues?.name?.toLowerCase();
                          const isDuplicate = existingSegmentNames.some(
                            (name) =>
                              name === value.toLowerCase() &&
                              name !== currentName,
                          );
                          return isDuplicate
                            ? Promise.reject(
                                new Error(
                                  "Сегмент с таким именем уже существует",
                                ),
                              )
                            : Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Введите название" />
                  </Form.Item>

                  {segmentType === "standard" && (
                    <>
                      <Divider orientation="left">Критерии сегмента</Divider>

                      <Collapse
                        defaultActiveKey={["purchases", "loyality", "extra"]}
                        style={{ border: "1px solid #d9d9d9", borderRadius: 8 }}
                      >
                        <Panel
                          header={
                            <span>
                              Параметры покупок{" "}
                              <span style={{ color: "#ff4d4f", fontSize: 14 }}>
                                *
                              </span>
                              {purchasesError && (
                                <span
                                  style={{
                                    color: "#ff4d4f",
                                    fontSize: 12,
                                    marginLeft: 8,
                                    fontWeight: "normal",
                                  }}
                                >
                                  — заполните хотя бы одно поле
                                </span>
                              )}
                            </span>
                          }
                          key="purchases"
                          className={`segment-purchases-panel${purchasesError ? " segment-purchases-error" : ""}`}
                          style={{ marginBottom: 8 }}
                        >
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Общая сумма покупок"
                                style={{ marginBottom: 8 }}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "total_amount",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="От"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "total_amount",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="До"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "total_amount",
                                      "eq",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Равно"
                                      style={{ width: "34%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                              <Form.Item
                                label="Количество покупок"
                                style={{ marginBottom: 8 }}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "count",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="От"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "count",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="До"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "count",
                                      "eq",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Равно"
                                      style={{ width: "34%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                              <Form.Item
                                label="Средний чек"
                                style={{ marginBottom: 8 }}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "amount_per_check",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="От"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "amount_per_check",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="До"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "amount_per_check",
                                      "eq",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Равно"
                                      style={{ width: "34%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                              <Form.Item
                                label="Последняя покупка (дней назад)"
                                name={[
                                  "criteria",
                                  "purchases",
                                  "last_purchase_days_ago",
                                ]}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "last_purchase_days_ago",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Не менее"
                                      style={{ width: "50%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "purchases",
                                      "last_purchase_days_ago",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Не более"
                                      style={{ width: "50%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Период покупок"
                                name={["criteria", "purchases", "date_range"]}
                              >
                                <DatePicker.RangePicker
                                  style={{ width: "100%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                label="Категории товаров"
                                name={["criteria", "purchases", "categories"]}
                              >
                                <Select
                                  mode="tags"
                                  placeholder="Выберите категории"
                                  tokenSeparators={[","]}
                                  allowClear
                                  options={categories.map((c) => ({
                                    value: c,
                                    label: c,
                                  }))}
                                />
                              </Form.Item>
                              <Form.Item
                                label="Конкретные товары"
                                name={[
                                  "criteria",
                                  "purchases",
                                  "nomenclatures",
                                ]}
                              >
                                <Select
                                  mode="tags"
                                  placeholder="Выберите товары"
                                  tokenSeparators={[","]}
                                  allowClear
                                  options={nomenclatures.map((n) => ({
                                    value: n,
                                    label: n,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>

                        <Panel header="Параметры лояльности" key="loyality">
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Баллы на счету"
                                style={{ marginBottom: 8 }}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "loyality",
                                      "balance",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="От"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "loyality",
                                      "balance",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="До"
                                      style={{ width: "33%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "loyality",
                                      "balance",
                                      "eq",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Равно"
                                      style={{ width: "34%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Сгорание баллов (дней)"
                                name={[
                                  "criteria",
                                  "loyality",
                                  "expires_in_days",
                                ]}
                              >
                                <Input.Group compact>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "loyality",
                                      "expires_in_days",
                                      "gte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Не менее"
                                      style={{ width: "50%" }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      "criteria",
                                      "loyality",
                                      "expires_in_days",
                                      "lte",
                                    ]}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Не более"
                                      style={{ width: "50%" }}
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>

                        <Panel header="Дополнительные критерии" key="extra">
                          <Form.Item label="Теги" name={["criteria", "tags"]}>
                            <Select
                              mode="tags"
                              placeholder="Введите теги"
                              tokenSeparators={[","]}
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item
                            label="Теги документов продаж"
                            name={["criteria", "docs_sales_tags"]}
                          >
                            <Select
                              mode="tags"
                              placeholder="Введите теги документов продаж"
                              tokenSeparators={[","]}
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item
                            label="Требуется доставка"
                            name={["criteria", "delivery_required"]}
                          >
                            <Segmented
                              options={[
                                { label: "Не задано", value: null },
                                { label: "Да", value: true },
                                { label: "Нет", value: false },
                              ]}
                              block
                            />
                          </Form.Item>
                          <Form.Item
                            label="Дата создания"
                            style={{ marginBottom: 8 }}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={["criteria", "created_at", "gte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="С даты"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={["criteria", "created_at", "lte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="По дату"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "created_at",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "created_at",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                          </Form.Item>
                        </Panel>

                        <Panel
                          header="Информация о доставке"
                          key="delivery_info"
                        >
                          <Form.Item
                            label="Адрес доставки"
                            name={["criteria", "delivery_info", "address"]}
                          >
                            <Input placeholder="Введите адрес" />
                          </Form.Item>
                          <Form.Item
                            label="Дата доставки"
                            name={[
                              "criteria",
                              "delivery_info",
                              "delivery_date",
                            ]}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "delivery_date",
                                  "gte",
                                ]}
                                noStyle
                              >
                                <DatePicker
                                  placeholder="С даты"
                                  style={{ width: "33%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "delivery_date",
                                  "lte",
                                ]}
                                noStyle
                              >
                                <DatePicker
                                  placeholder="По дату"
                                  style={{ width: "33%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "delivery_date",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "17%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "delivery_date",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "17%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                          </Form.Item>
                          <Form.Item
                            label="Примечание"
                            name={["criteria", "delivery_info", "note"]}
                          >
                            <Input.TextArea placeholder="Введите примечание" />
                          </Form.Item>
                          <Divider orientation="left">Получатель</Divider>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item
                                label="Имя"
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "recipient",
                                  "name",
                                ]}
                              >
                                <Input placeholder="Имя получателя" />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Фамилия"
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "recipient",
                                  "surname",
                                ]}
                              >
                                <Input placeholder="Фамилия получателя" />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Телефон"
                                name={[
                                  "criteria",
                                  "delivery_info",
                                  "recipient",
                                  "phone",
                                ]}
                              >
                                <Input placeholder="Телефон получателя" />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>

                        <Panel header="Сборщик" key="picker">
                          <Form.Item
                            name={["criteria", "picker", "assigned"]}
                            label="Назначен"
                          >
                            <Segmented
                              options={[
                                { label: "Не задано", value: null },
                                { label: "Да", value: true },
                                { label: "Нет", value: false },
                              ]}
                              block
                            />
                          </Form.Item>
                          <Form.Item
                            label="Сборщик начал"
                            style={{ marginBottom: 8 }}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={["criteria", "picker", "start", "gte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="С даты (gte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={["criteria", "picker", "start", "lte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="По дату (lte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "picker",
                                  "start",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "picker",
                                  "start",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                          </Form.Item>
                          <Form.Item
                            label="Сборщик закончил"
                            style={{ marginBottom: 8 }}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={["criteria", "picker", "finish", "gte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="С даты (gte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={["criteria", "picker", "finish", "lte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="По дату (lte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "picker",
                                  "finish",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "picker",
                                  "finish",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                            <Form.Item
                              label="Сборщик не добавил фотографии позже (минуты):"
                              name={[
                                "criteria",
                                "picker",
                                "photos_not_added_minutes",
                              ]}
                              style={{ marginTop: 8 }}
                            >
                              <InputNumber
                                placeholder="Введите минуты"
                                min={1}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Form.Item>
                        </Panel>

                        <Panel header="Курьер" key="courier">
                          <Form.Item
                            name={["criteria", "courier", "assigned"]}
                            label="Назначен"
                          >
                            <Segmented
                              options={[
                                { label: "Не задано", value: null },
                                { label: "Да", value: true },
                                { label: "Нет", value: false },
                              ]}
                              block
                            />
                          </Form.Item>
                          <Form.Item
                            label="Курьер начал"
                            style={{ marginBottom: 8 }}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={["criteria", "courier", "start", "gte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="С даты (gte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={["criteria", "courier", "start", "lte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="По дату (lte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "courier",
                                  "start",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "courier",
                                  "start",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                          </Form.Item>
                          <Form.Item
                            label="Курьер закончил"
                            style={{ marginBottom: 8 }}
                          >
                            <Input.Group compact>
                              <Form.Item
                                name={["criteria", "courier", "finish", "gte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="С даты (gte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={["criteria", "courier", "finish", "lte"]}
                                noStyle
                                getValueProps={(v) => ({
                                  value: v && v.isValid?.() ? v : null,
                                })}
                              >
                                <DatePicker
                                  placeholder="По дату (lte)"
                                  style={{ width: "30%" }}
                                  format="YYYY-MM-DD"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "courier",
                                  "finish",
                                  "gte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (до)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[
                                  "criteria",
                                  "courier",
                                  "finish",
                                  "lte_seconds_ago",
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  placeholder="Сек. назад (от)"
                                  style={{ width: "20%" }}
                                />
                              </Form.Item>
                            </Input.Group>
                          </Form.Item>
                        </Panel>
                      </Collapse>

                      <Collapse style={{ marginTop: 16 }}>
                        <Panel
                          header={
                            <span style={{ color: "orange" }}>Действия</span>
                          }
                          key="actions"
                        >
                          <Tabs
                            type="card"
                            size="small"
                            items={[
                              {
                                label: "Основные теги",
                                key: "basic_tags",
                                children: (
                                  <>
                                    <Form.Item
                                      label="Добавить существующие теги"
                                      name={[
                                        "actions",
                                        "add_existed_tags",
                                        "name",
                                      ]}
                                    >
                                      <Select
                                        mode="tags"
                                        placeholder="Введите теги для добавления"
                                        tokenSeparators={[","]}
                                        allowClear
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Удалить теги"
                                      name={["actions", "remove_tags", "name"]}
                                    >
                                      <Select
                                        mode="tags"
                                        placeholder="Введите теги для удаления"
                                        tokenSeparators={[","]}
                                        allowClear
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Клиентские теги (client_tags)"
                                      shouldUpdate
                                    >
                                      {() => {
                                        const tags =
                                          form.getFieldValue([
                                            "actions",
                                            "client_tags",
                                            "tags",
                                          ]) || [];
                                        return (
                                          <div>
                                            {tags.map((tag, idx) => (
                                              <Input.Group
                                                compact
                                                key={idx}
                                                style={{ marginBottom: 8 }}
                                              >
                                                <Form.Item
                                                  name={[
                                                    "actions",
                                                    "client_tags",
                                                    "tags",
                                                    idx,
                                                    "name",
                                                  ]}
                                                  noStyle
                                                >
                                                  <Input
                                                    placeholder="Имя"
                                                    style={{ width: "25%" }}
                                                  />
                                                </Form.Item>
                                                <Form.Item
                                                  name={[
                                                    "actions",
                                                    "client_tags",
                                                    "tags",
                                                    idx,
                                                    "emoji",
                                                  ]}
                                                  noStyle
                                                >
                                                  <Input
                                                    placeholder="Эмоджи"
                                                    style={{ width: "15%" }}
                                                  />
                                                </Form.Item>
                                                <Form.Item
                                                  name={[
                                                    "actions",
                                                    "client_tags",
                                                    "tags",
                                                    idx,
                                                    "color",
                                                  ]}
                                                  noStyle
                                                  getValueFromEvent={(color) =>
                                                    typeof color === "string"
                                                      ? color
                                                      : color?.toHexString?.() ||
                                                        ""
                                                  }
                                                >
                                                  <ColorPicker
                                                    showText
                                                    format="hex"
                                                  />
                                                </Form.Item>
                                                <Form.Item
                                                  name={[
                                                    "actions",
                                                    "client_tags",
                                                    "tags",
                                                    idx,
                                                    "description",
                                                  ]}
                                                  noStyle
                                                >
                                                  <Input
                                                    placeholder="Описание"
                                                    style={{ width: "30%" }}
                                                  />
                                                </Form.Item>
                                                <Button
                                                  danger
                                                  onClick={() => {
                                                    const newTags = [...tags];
                                                    newTags.splice(idx, 1);
                                                    form.setFieldsValue({
                                                      actions: {
                                                        client_tags: {
                                                          tags: newTags,
                                                        },
                                                      },
                                                    });
                                                  }}
                                                >
                                                  Удалить
                                                </Button>
                                              </Input.Group>
                                            ))}
                                            <Button
                                              type="dashed"
                                              onClick={() =>
                                                form.setFieldsValue({
                                                  actions: {
                                                    client_tags: {
                                                      tags: [
                                                        ...tags,
                                                        {
                                                          name: "",
                                                          emoji: "",
                                                          color: "#038bDB",
                                                          description: "",
                                                        },
                                                      ],
                                                    },
                                                  },
                                                })
                                              }
                                            >
                                              Добавить тег
                                            </Button>
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                    <Form.Item
                                      label="Добавить теги документов продаж"
                                      name={[
                                        "actions",
                                        "add_docs_sales_tags",
                                        "tags",
                                      ]}
                                    >
                                      <Select
                                        mode="tags"
                                        placeholder="Введите теги для добавления"
                                        tokenSeparators={[","]}
                                        allowClear
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Удалить теги документов продаж"
                                      name={[
                                        "actions",
                                        "remove_docs_sales_tags",
                                        "tags",
                                      ]}
                                    >
                                      <Select
                                        mode="tags"
                                        placeholder="Введите теги для удаления"
                                        tokenSeparators={[","]}
                                        allowClear
                                      />
                                    </Form.Item>
                                  </>
                                ),
                              },
                              {
                                label: "TG Уведомления",
                                key: "tg_notifications",
                                children: (
                                  <>
                                    <Form.Item
                                      name={[
                                        "actions",
                                        "send_tg_notification",
                                        "trigger_on_new",
                                      ]}
                                      label="TG-уведомление: включить триггер при новом"
                                    >
                                      <Segmented
                                        options={[
                                          { label: "Не задано", value: null },
                                          { label: "Да", value: true },
                                          { label: "Нет", value: false },
                                        ]}
                                        block
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="TG-уведомление: сообщение"
                                      name={[
                                        "actions",
                                        "send_tg_notification",
                                        "message",
                                      ]}
                                    >
                                      <Input placeholder="Текст сообщения" />
                                    </Form.Item>
                                    <Form.Item
                                      name={[
                                        "actions",
                                        "send_tg_notification",
                                        "shift_status",
                                      ]}
                                      label="TG-уведомление: для сотрудников"
                                    >
                                      <Segmented
                                        options={[
                                          { label: "Не задано", value: null },
                                          {
                                            label: "На смене",
                                            value: "on_shift",
                                          },
                                          {
                                            label: "Не на смене",
                                            value: "off_shift",
                                          },
                                          {
                                            label: "На перерыве",
                                            value: "on_break",
                                          },
                                        ]}
                                        block
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="TG-уведомление: user_tag"
                                      name={[
                                        "actions",
                                        "send_tg_notification",
                                        "user_tag",
                                      ]}
                                    >
                                      <Input placeholder="User tag" />
                                    </Form.Item>
                                    <Form.Item
                                      label="TG-уведомление: отправить кому"
                                      name={[
                                        "actions",
                                        "send_tg_notification",
                                        "send_to",
                                      ]}
                                    >
                                      <Select
                                        options={[
                                          { value: "picker", label: "Сборщик" },
                                          { value: "courier", label: "Курьер" },
                                        ]}
                                      />
                                    </Form.Item>
                                  </>
                                ),
                              },
                              {
                                label: "Карты лояльности",
                                key: "loyality_cards",
                                children: (
                                  <>
                                    <Form.Item
                                      label="Процент кэшбека"
                                      name={[
                                        "actions",
                                        "transform_loyality_card",
                                        "cashback_percent",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Максимальный процент списания"
                                      name={[
                                        "actions",
                                        "transform_loyality_card",
                                        "max_withdraw_percentage",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Время жизни (секунды)"
                                      name={[
                                        "actions",
                                        "transform_loyality_card",
                                        "lifetime",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Тег карты"
                                      name={[
                                        "actions",
                                        "transform_loyality_card",
                                        "tag",
                                      ]}
                                    >
                                      <Input placeholder="Введите тег" />
                                    </Form.Item>
                                    <Form.Item
                                      label="Реклама для Apple Wallet"
                                      name={[
                                        "actions",
                                        "transform_loyality_card",
                                        "apple_wallet_advertisement",
                                      ]}
                                    >
                                      <TextArea placeholder="TableCRM" />
                                    </Form.Item>
                                  </>
                                ),
                              },
                              {
                                label: "Транзакции лояльности",
                                key: "loyality_transactions",
                                children: (
                                  <>
                                    <Form.Item
                                      label="Сумма транзакции"
                                      name={[
                                        "actions",
                                        "add_loyality_transaction",
                                        "amount",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Направление"
                                      name={[
                                        "actions",
                                        "add_loyality_transaction",
                                        "direction",
                                      ]}
                                    >
                                      <Select>
                                        <Select.Option value="plus">
                                          Начисление (+)
                                        </Select.Option>
                                        <Select.Option value="minus">
                                          Списание (-)
                                        </Select.Option>
                                      </Select>
                                    </Form.Item>
                                    <Form.Item
                                      label="Комментарий"
                                      name={[
                                        "actions",
                                        "add_loyality_transaction",
                                        "comment",
                                      ]}
                                    >
                                      <Input placeholder="Комментарий к транзакции" />
                                    </Form.Item>
                                  </>
                                ),
                              },
                              {
                                label: "WhatsApp",
                                key: "whatsapp",
                                children: (
                                  <>
                                    <Form.Item
                                      name={[
                                        "actions",
                                        "send_wa_notification",
                                        "trigger_on_new",
                                      ]}
                                      valuePropName="checked"
                                    >
                                      <Checkbox>
                                        Триггер при новом контрагенте
                                      </Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                      label="Сообщение"
                                      name={[
                                        "actions",
                                        "send_wa_notification",
                                        "message",
                                      ]}
                                    >
                                      <Input.TextArea placeholder="Текст WhatsApp сообщения" />
                                    </Form.Item>
                                    <Form.Item
                                      label="Wappi Token"
                                      name={[
                                        "actions",
                                        "send_wa_notification",
                                        "wappi_token",
                                      ]}
                                    >
                                      <Input placeholder="Токен Wappi" />
                                    </Form.Item>
                                    <Form.Item
                                      label="Wappi Profile ID"
                                      name={[
                                        "actions",
                                        "send_wa_notification",
                                        "wappi_profile_id",
                                      ]}
                                    >
                                      <Input placeholder="ID профиля Wappi" />
                                    </Form.Item>
                                    <Form.Item
                                      label="Задержка (секунды)"
                                      name={[
                                        "actions",
                                        "send_wa_notification",
                                        "sleep",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                  </>
                                ),
                              },
                              {
                                label: "HTTP Запросы",
                                key: "http_requests",
                                children: (
                                  <>
                                    <Form.Item
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "trigger_on_new",
                                      ]}
                                      valuePropName="checked"
                                    >
                                      <Checkbox>
                                        Триггер при новом контрагенте
                                      </Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                      label="Метод"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "method",
                                      ]}
                                    >
                                      <Select>
                                        <Select.Option value="GET">
                                          GET
                                        </Select.Option>
                                        <Select.Option value="POST">
                                          POST
                                        </Select.Option>
                                        <Select.Option value="PUT">
                                          PUT
                                        </Select.Option>
                                        <Select.Option value="DELETE">
                                          DELETE
                                        </Select.Option>
                                      </Select>
                                    </Form.Item>
                                    <Form.Item
                                      label="URL"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "url",
                                      ]}
                                    >
                                      <Input placeholder="https://example.com/api" />
                                    </Form.Item>
                                    <Form.Item
                                      label="Заголовки (JSON)"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "headers",
                                      ]}
                                    >
                                      <Input.TextArea
                                        placeholder='{"Content-Type": "application/json"}'
                                        rows={2}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Параметры (JSON)"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "params",
                                      ]}
                                    >
                                      <Input.TextArea
                                        placeholder='{"param": "value"}'
                                        rows={2}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Тело запроса (JSON)"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "body",
                                      ]}
                                    >
                                      <Input.TextArea
                                        placeholder='{"key": "value"}'
                                        rows={2}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      label="Задержка (секунды)"
                                      name={[
                                        "actions",
                                        "do_http_request",
                                        "sleep",
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                  </>
                                ),
                              },
                            ]}
                          />
                        </Panel>
                      </Collapse>

                      <Divider orientation="left">Настройки обновления</Divider>

                      <Form.Item name="type_of_update" label="Тип обновления">
                        <Radio.Group
                          onChange={(e) => setUpdateType(e.target.value)}
                        >
                          <Radio value="request">Вручную</Radio>
                          <Radio value="cron">По расписанию</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {updateType === "cron" && (
                        <Form.Item
                          label="Интервал обновления (минуты)"
                          name={["update_settings", "interval_minutes"]}
                          rules={[
                            { required: true, message: "Укажите интервал" },
                          ]}
                        >
                          <InputNumber
                            min={1}
                            style={{ width: "100%" }}
                            onFocus={() => handleFieldFocus("update_settings")}
                          />
                        </Form.Item>
                      )}

                      <Form.Item name="is_archived" valuePropName="checked">
                        <Checkbox>Архивировать сегмент</Checkbox>
                      </Form.Item>
                    </>
                  )}
                </Form>
              ),
            },
            ...(segmentType === "auto_reply"
              ? [
                  {
                    key: "auto_reply_tab",
                    label: "Автоответ",
                    children: renderAutoReplyTab(),
                  },
                ]
              : []),
            {
              key: "json",
              label: "JSON",
              children: (
                <>
                  {jsonError && (
                    <Alert
                      message={jsonError}
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <TextArea
                    rows={20}
                    value={jsonValue}
                    onChange={handleJsonChange}
                    style={{ fontFamily: "monospace", fontSize: 14 }}
                  />
                  <div style={{ marginTop: 8, color: "#666" }}>
                    <small>
                      Изменения в JSON автоматически синхронизируются с формой
                    </small>
                  </div>
                </>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default CreateSegments;
